import {
  CtaState,
  DenominationGridInternalState,
} from "~/components/denomination_grid/denominations_grid";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { VoucherProduct } from "~/server/types/brand";
import {
  collectPaymentViaRazorpayCustomCheckout,
  RazorpayCheckoutProps,
  RazorpayMode,
} from "~/utils/third_party/razorpay";
import { showSnackBar } from "~/shared_states/snackbar";
import { useWebStoreContext } from "~/context/webstore_context";
import { config } from "~/data/config";
import { goBack, openModalV2 } from "~/shared_states/modal";
import { Accessor, createMemo, createSignal } from "solid-js";
import { Header, Optional } from "~/types";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { ErrorMessage } from "~/data/consts";
import { generateDeviceVerificationToken } from "~/utils/common";
import { razorpayTDConfig } from "~/utils/razorpay_configs";
import { collectCardDetails } from "~/components/card_details_form";
import { useNavigate } from "@solidjs/router";

// these are incorrect imports, need to fix them
import useTataUserInfoModal from "~/views/webstore/brandl2_tata_digital/hooks/useTataUserInfoModal";
import { tataNeuCardValidator } from "~/views/webstore/brandl2_tata_digital/utils/tata_neu_card_validator";
import { getBrandL2OtpVerificationComponent } from "~/views/webstore/brandl2_tata_digital/fragments/verify_otp";

export interface OpenPaymentLoaderProps {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}

// this hook uses webstore context, should not be used outside of webstore
// this hook is used for only rnr payment, not for webstore payment
// so the user is always logged in
export function useTataPayment(props: {
  brand: () => VoucherProduct;
  brandKey: string;
  isOfferConstructAvailable: boolean;
  offerConstructType: "ONE_TIME_REWARD" | "SUBSCRIPTION_REWARD" | undefined;
}) {
  const brandKey = props.brandKey;
  const [ctaState, setCtaState] = createSignal<CtaState>("DEFAULT");
  const [showFullPageLoadingOverlay, setShowFullPageLoadingOverlay] =
    createSignal(false);
  const navigate = useNavigate();

  const brand = props.brand;
  // Store current internal state from denomination grid
  let currentInternalState: DenominationGridInternalState | null = null;

  const analyticsService = useAnalyticsService();
  const userInfoModal = useTataUserInfoModal();

  const { storeState, refreshV4HomePage } = useWebStoreContext();

  // used to override the default denomination list
  function getDenominationList(brand: VoucherProduct) {
    const customAmountDefaultDenominationsList = [
      100, 200, 500, 1000, 2000, 5000,
    ].filter(
      (denomination) =>
        denomination >= brand.amountConditions.minAmount &&
        denomination <= brand.amountConditions.maxAmount
    );

    let d =
      brand.amountConditions.denominations ??
      customAmountDefaultDenominationsList;

    return d.slice(0, 2);
  }

  const onClickGetThisCard: (
    internalState: DenominationGridInternalState
  ) => Promise<void> = async (internalState: DenominationGridInternalState) => {
    // Store internal state for use in order initiation
    currentInternalState = internalState;

    if (
      storeState.v4HomePage?.user &&
      storeState.v4HomePage?.user?.phoneNumber == null
    ) {
      userInfoModal.openModal({
        isOfferConstructAvailable: props.isOfferConstructAvailable,
        offerConstructType: props.offerConstructType,
        offerConstructTenure: currentInternalState.offerConstructTenure,
        clientLogoUrl: storeState.routeData?.clientConfig?.clientLogoUrl,
        redemptionAmount: currentInternalState.redemptionAmount,
        // do not maintain dueAmount2 in internal state, tenur can change: do not depend on hardcoded value
        dueAmount: getDueAmount(currentInternalState),
        brandDetails: brand()?.voucherProductMetadata,
        selectedAmount: currentInternalState.selectedAmount,
        email: storeState.v4HomePage?.user?.email ?? null,
        phone: storeState.v4HomePage?.user?.phoneNumber ?? null,
        name: fullUserName(),
        onClickSubmit: onProfileFormSubmit,
        subtitle: getUserDataCollectionModalSubtitle(),
      });
    } else {
      await initiateOrder();
    }
  };

  function getDueAmount(
    currentInternalState: DenominationGridInternalState
  ): Accessor<Optional<number>> {
    if (props.offerConstructType === "SUBSCRIPTION_REWARD") {
      return currentInternalState.offerConstructTenure() == 6
        ? currentInternalState.dueAmount
        : currentInternalState.dueAmount2;
    }
    return currentInternalState?.dueAmount;
  }

  async function onProfileFormSubmit(profileUpdateParams: any) {
    analyticsService.logEvent("submit_user_details_in_brandl2", {
      brandName: brand()?.name.toLowerCase(),
      ...profileUpdateParams,
    });

    const updateUserProfile = async () => {
      userInfoModal.setShowLoader(true);
      try {
        await clientRepo.updatePartnerUserProfile(profileUpdateParams);
        await refreshV4HomePage();
        await userInfoModal.closeModal();
        await initiateOrder();
      } catch (e: any) {
        showSnackBar({
          level: "error",
          message: e.message ?? "Something went wrong",
        });
      } finally {
        userInfoModal.setShowLoader(false);
      }
    };

    const sendOtp = async () => {
      return await clientRepo.initiatePhoneUpdate(
        profileUpdateParams.phoneNumber
      );
    };
    try {
      userInfoModal.setShowLoader(true);
      const otpToken = await sendOtp();
      userInfoModal.setShowLoader(false);

      openModalV2(() =>
        getBrandL2OtpVerificationComponent({
          phoneNumber: profileUpdateParams.phoneNumber,
          otpToken: otpToken.otpToken,
          sendOtp: sendOtp,
          onVerificationSuccess: async () => {
            analyticsService.logEvent("otp_verification_success_in_brandl2", {
              brandName: brand()?.name.toLowerCase(),
              ...profileUpdateParams,
            });
            goBack();
            await updateUserProfile();
          },
          onError: (error) => {
            showSnackBar({
              level: "error",
              message: error ?? "Something went wrong",
            });
          },
        })
      );
    } catch (error: any) {
      userInfoModal.setShowLoader(false);
      showSnackBar({
        level: "error",
        message: error.message ?? "Something went wrong",
      });
    }
  }

  function fullUserName() {
    let user = storeState.v4HomePage?.user;
    return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  }

  function getUserDataCollectionModalSubtitle() {
    return "Card details will be sent on your whatsapp & email";
  }

  const initiateOrder = async () => {
    try {
      setCtaState("LOADING");

      // Use amount from internal state if available, fallback to simulation data
      const amount = currentInternalState?.redemptionAmount();
      // TODO: add a logic to get fallback or make currentInternalState non nullable
      // simulateTransactionDetails()?.redemptionAmount;
      if (!amount) {
        throw new Error("Can't initiate order when amount is null");
      }

      let headers: Record<string, any> = {};
      if (storeState.v4HomePage?.user.fraudChecksEnabled) {
        const token = await generateDeviceVerificationToken();
        if (!token) {
          const err = new Error("FPJS token is undefined");
          captureErrorInSentry(err);
          showSnackBar({
            message: ErrorMessage.SECURITY_CHECK_FAILED,
            level: "error",
          });
          throw err;
        }
        headers[Header.FpDeviceToken] = token;
      }

      const initiateRedemptionResponse = await clientRepo.initiateRedemption(
        {
          amount: amount,
          paymentMode: "UPI",
          productId: brand()?.externalId!,
          ...(brand()?.amountConditions.denominations != null
            ? {
                denominationDetails: [
                  {
                    denomination: amount,
                    quantity: 1,
                  },
                ],
              }
            : {}),
          variantId:
            currentInternalState?.selectedVariantId() ??
            brand()?.variantDetailedResponse?.find(
              (variant) => variant.isDefault
            )?.variantId ??
            "",
          consumeCoins: currentInternalState?.useRewardCoins() ?? true,
          ...(props.offerConstructType === "SUBSCRIPTION_REWARD" && {
            subscriptionDetails: {
              tenure: currentInternalState?.offerConstructTenure() ?? 6,
              // TODO: add a logic to get fallback or make currentInternalState non nullable
              // offerConstruct()?.subscriptionRewardDetails?.find(
              //   (detail: any) => detail.isDefault
              // )?.tenure ??
              // offerConstruct()?.subscriptionRewardDetails?.[0]?.tenure,
            },
          }),
        },
        headers
      );

      analyticsService.logEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: brand()!.name.toLowerCase(),
        amount: amount,
        consumeCoins: currentInternalState?.useRewardCoins() ?? true,
        productId: brand()!.externalId,
      });

      function onPaymentFailed(failureReason: string) {
        analyticsService.logEvent(HubbleEvent.PAYMENT_FAIL, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
          reason: failureReason,
        });

        const link =
          `/buy-gift-card/${brand()!.brandKey}/order` +
          `?orderId=${initiateRedemptionResponse.storeOrderDetails.id}` +
          `&paymentFailureReason=${failureReason}`;
        window.location.href = link;
      }

      function onPaymentComplete() {
        analyticsService.logEvent(HubbleEvent.PAYMENT_SUCCESS, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
        });

        const orderId = encodeURIComponent(
          initiateRedemptionResponse.storeOrderDetails.id
        );
        const brandKey = encodeURIComponent(brand()!.brandKey);

        navigate(`/buy-gift-card/${brandKey}/order?orderId=${orderId}`, {
          resolve: true,
        });
      }

      async function collectPaymentViaRazorpay(_props: {
        depositId: string;
        amount: number;
      }) {
        let order;
        if (props.offerConstructType !== "SUBSCRIPTION_REWARD") {
          order = await clientRepo.payOrderStandardCheckout(_props.depositId);
        }

        let razorpayCheckoutParams: RazorpayCheckoutProps = {
          amount: _props.amount,
          email: storeState.v4HomePage?.user?.email ?? "",
          orderId: order
            ? order.providerOrderId
            : (initiateRedemptionResponse?.depositPaymentDetails
                .providerMetadata?.providerOrderId ?? ""),
          phone: storeState.v4HomePage?.user?.phoneNumber ?? "",
          onSuccess: onPaymentComplete,
          onFailure: (response) => {
            onPaymentFailed(response.error.description);
          },
          mode: "UPI" as RazorpayMode,
        };

        razorpayCheckoutParams = {
          ...razorpayCheckoutParams,
          key: config.tdSubscriptionRazorpayKey,
          mode: "CARD",
          config: {
            prefill: {
              email: storeState.v4HomePage?.user?.email ?? "",
              contact: storeState.v4HomePage?.user?.phoneNumber ?? "",
              method: "card",
            },
            config: razorpayTDConfig.onlyCardConfigForTD,
          },

          customer_id:
            initiateRedemptionResponse?.depositPaymentDetails.providerMetadata
              ?.customerId ?? "",
        };

        if (props.offerConstructType === "SUBSCRIPTION_REWARD") {
          razorpayCheckoutParams = {
            ...razorpayCheckoutParams,
            recurring: "1",
          };
        }

        collectCardDetails({
          onSubmit: async (cardDetails) => {
            let event_params = {
              ...razorpayCheckoutParams,
              brandName: brand()?.name.toLowerCase(),
            };
            delete event_params.onSuccess;
            delete event_params.onFailure;

            analyticsService.logEvent(
              "collect_card_details_in_brandl2",
              event_params
            );
            setShowFullPageLoadingOverlay(true);
            collectPaymentViaRazorpayCustomCheckout({
              ...razorpayCheckoutParams,
              cardDetails: cardDetails,
            });
          },
          cardNumberLiveValidator:
            config.env === "Prod" ? tataNeuCardValidator : undefined,
          title: "Enter your NeuCard details",
        });
      }

      if (
        initiateRedemptionResponse.depositPaymentDetails.status == "SUCCESS"
      ) {
        onPaymentComplete();
      } else {
        await collectPaymentViaRazorpay({
          depositId: initiateRedemptionResponse.depositPaymentDetails.id,
          amount: initiateRedemptionResponse.depositPaymentDetails.amount,
        });
      }
      // State management moved to denomination grid
      setCtaState("DEFAULT");
    } catch (error: any) {
      showSnackBar({
        message: error.message,
        level: "error",
      });
      setCtaState("DEFAULT");

      // State management moved to denomination grid
    }
  };

  return {
    onClickGetThisCard,
    getDenominationList,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
  };
}
